import { Flags, getLocalStorageKey } from "@/feature-flags";
import { useSegmentTrack } from "@/hooks/use-segment";
import { useOrganization } from "@/queries/organization-queries";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useSyncExternalStore } from "react";

/**
 * Registry of known experiments. Only experiments listed here can be activated.
 * This prevents backend load from random URL visits.
 */
export const KNOWN_EXPERIMENTS = {
    ralph: {
        flag: Flags.RalphModeEnabled,
        name: "Ralph Mode",
        description: "Run Ona in a loop to autonomously build software while you're AFK",
    },
} as const;

export type ExperimentName = keyof typeof KNOWN_EXPERIMENTS;

const PENDING_EXPERIMENT_KEY = "experiment_pending_activation";

function isKnownExperiment(name: string): name is ExperimentName {
    return name in KNOWN_EXPERIMENTS;
}

export function getExperiment(name: string) {
    if (!isKnownExperiment(name)) {
        return null;
    }
    return KNOWN_EXPERIMENTS[name];
}

/**
 * Store a pending experiment to be activated after login/onboarding.
 */
export function setPendingExperiment(name: string) {
    if (isKnownExperiment(name)) {
        try {
            localStorage.setItem(PENDING_EXPERIMENT_KEY, name);
        } catch {
            // Ignore localStorage errors
        }
    }
}

/**
 * Get any pending experiment that should be activated.
 */
export function getPendingExperiment(): string | null {
    try {
        return localStorage.getItem(PENDING_EXPERIMENT_KEY);
    } catch {
        return null;
    }
}

/**
 * Clear the pending experiment after it's been processed.
 */
export function clearPendingExperiment() {
    try {
        localStorage.removeItem(PENDING_EXPERIMENT_KEY);
    } catch {
        // Ignore localStorage errors
    }
}

/**
 * Activate an experiment by setting its flag in localStorage.
 */
export function activateExperiment(name: ExperimentName) {
    const experiment = KNOWN_EXPERIMENTS[name];
    const storageKey = getLocalStorageKey(experiment.flag);
    setLocalStorageValue(storageKey, true);
}

/**
 * Subscribe to localStorage changes for a specific key.
 * This enables React to re-render when localStorage changes.
 */
function createLocalStorageSubscription(key: string) {
    return (callback: () => void) => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === key) {
                callback();
            }
        };
        window.addEventListener("storage", handleStorageChange);
        // Also listen for custom events for same-tab updates
        const handleCustomEvent = () => callback();
        window.addEventListener(`localStorage:${key}`, handleCustomEvent);
        return () => {
            window.removeEventListener("storage", handleStorageChange);
            window.removeEventListener(`localStorage:${key}`, handleCustomEvent);
        };
    };
}

function getLocalStorageValue(key: string): boolean {
    try {
        const value = localStorage.getItem(key);
        if (value === null) return false;
        return JSON.parse(value) === true;
    } catch {
        return false;
    }
}

function setLocalStorageValue(key: string, value: boolean) {
    try {
        localStorage.setItem(key, JSON.stringify(value));
        // Dispatch custom event for same-tab updates
        window.dispatchEvent(new Event(`localStorage:${key}`));
    } catch (e) {
        console.warn(`Failed to set localStorage value for ${key}`, e);
    }
}

/**
 * Hook to check if the current organization can use experiments.
 * Only enterprise tier is excluded - all other tiers (FREE, FREE_ONA, CORE) can use experiments.
 */
export function useCanUseExperiments() {
    const { data: organization, isLoading } = useOrganization();

    const isEnterprise = organization?.tier === OrganizationTier.ENTERPRISE;
    const canUse = !isEnterprise;

    return {
        canUse,
        isEnterprise,
        loading: isLoading,
    };
}

/**
 * Hook to manage a specific experiment's enabled state.
 */
export function useExperiment(experimentName: ExperimentName) {
    const experiment = KNOWN_EXPERIMENTS[experimentName];
    const storageKey = getLocalStorageKey(experiment.flag);

    const isEnabled = useSyncExternalStore(
        createLocalStorageSubscription(storageKey),
        () => getLocalStorageValue(storageKey),
        () => false, // Server snapshot
    );

    const setEnabled = useCallback(
        (enabled: boolean) => {
            setLocalStorageValue(storageKey, enabled);
        },
        [storageKey],
    );

    return {
        isEnabled,
        setEnabled,
        experiment,
    };
}

/**
 * Hook to track usage of an experiment feature.
 * Call this when a user interacts with a feature that's part of an experiment.
 *
 * @example
 * const trackRalphUsage = useTrackExperimentUsage("ralph");
 * // When user uses a Ralph mode feature:
 * trackRalphUsage("ralph_feature_clicked", { feature: "some_feature" });
 */
export function useTrackExperimentUsage(experimentName: ExperimentName) {
    const { isEnabled } = useExperiment(experimentName);
    const experiment = KNOWN_EXPERIMENTS[experimentName];
    const segmentTrack = useSegmentTrack();

    return useCallback(
        (action: string, properties?: Record<string, unknown>) => {
            if (!isEnabled) return;

            segmentTrack("experiment_feature_used", {
                experiment_name: experimentName,
                experiment_display_name: experiment.name,
                action,
                ...properties,
            });
        },
        [isEnabled, experimentName, experiment.name, segmentTrack],
    );
}
